// ============================================
// UI Store - Loading, Errors, Modals
// Single Responsibility: UI state management
// ============================================

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

interface UIState {
    // Global loading
    isLoading: boolean;

    // Errors
    error: string | null;

    // Modals/Overlays
    isModalOpen: boolean;
    modalContent: React.ReactNode | null;

    // Toast notifications
    toasts: Toast[];

    // Actions
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    clearError: () => void;
    openModal: (content: React.ReactNode) => void;
    closeModal: () => void;
    addToast: (toast: Omit<Toast, 'id'>) => void;
    removeToast: (id: string) => void;
}

export interface Toast {
    id: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
    duration?: number;
}

export const useUIStore = create<UIState>()(
    immer((set) => ({
        isLoading: false,
        error: null,
        isModalOpen: false,
        modalContent: null,
        toasts: [],

        setLoading: (loading) => set((state) => {
            state.isLoading = loading;
        }),

        setError: (error) => set((state) => {
            state.error = error;
        }),

        clearError: () => set((state) => {
            state.error = null;
        }),

        openModal: (content) => set((state) => {
            state.isModalOpen = true;
            state.modalContent = content;
        }),

        closeModal: () => set((state) => {
            state.isModalOpen = false;
            state.modalContent = null;
        }),

        addToast: (toast) => set((state) => {
            const id = `toast-${Date.now()}-${Math.random()}`;
            state.toasts.push({ ...toast, id });
        }),

        removeToast: (id) => set((state) => {
            state.toasts = state.toasts.filter(t => t.id !== id);
        }),
    }))
);
